import { useState } from 'react'
import {
  App, Button, Card, Col, InputNumber, Row, Select, Space, Statistic, Tag, Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'

import {
  ageingApi,
  downloadAgeingCsv,
  type AgeingBucket,
  type ItemAgeingRow,
  type LotAgeingRow,
  type MachineSpareAgeingRow,
} from '@/features/inventory/ageingApi'

// Union row type — this page renders three different shapes through one table.
type AnyRow = ItemAgeingRow | LotAgeingRow | MachineSpareAgeingRow
import { departmentsApi } from '@/features/masters/departments/api'
import { machinesApi } from '@/features/masters/machines/api'
import DataTable from '@/shared/components/DataTable'
import PageHeader from '@/shared/components/PageHeader'
import { usePlantId } from '@/shared/hooks/usePlantId'
import AgeingBucketBadge from './components/AgeingBucketBadge'

const { Text } = Typography

const BUCKETS: AgeingBucket[] = ['0-30', '31-60', '61-90', '91-180', '181-365', '365+']

/**
 * Generic ageing report. Used for several routes by tweaking
 * `flavour` — item-wise / lot-wise / slow-moving / dead-stock /
 * critical / high-value / machine-wise. All math is server-side; the
 * page just renders columns + an Export button.
 */
export interface AgeingReportProps {
  flavour:
    | 'item-wise' | 'lot-wise' | 'slow-moving' | 'dead-stock'
    | 'critical' | 'high-value' | 'machine-wise'
  title: string
  subtitle: string
}

export default function AgeingReportPage({
  flavour, title, subtitle,
}: AgeingReportProps) {
  const { message } = App.useApp()
  const { plantId } = usePlantId()
  const [bucket, setBucket] = useState<AgeingBucket | undefined>()
  const [departmentId, setDepartmentId] = useState<string | undefined>()
  const [machineId, setMachineId] = useState<string | undefined>()
  const [minValue, setMinValue] = useState<number>(10000)

  const { data: depts } = useQuery({
    queryKey: ['departments-all'],
    queryFn: () => departmentsApi.list({ page: 1, page_size: 200 }),
    staleTime: 5 * 60_000,
  })
  const { data: machines } = useQuery({
    queryKey: ['machines-all', plantId],
    enabled: !!plantId,
    queryFn: () => machinesApi.list({
      plant_id: plantId, is_active: true, page: 1, page_size: 500,
    }),
    staleTime: 5 * 60_000,
  })

  // ── Data fetch per flavour ─────────────────────────────────────────────
  const itemRows = useQuery<ItemAgeingRow[]>({
    enabled: flavour === 'item-wise',
    queryKey: ['ageing-item', bucket, departmentId, machineId],
    queryFn: () => ageingApi.itemWise({
      bucket, department_id: departmentId, machine_id: machineId,
    }),
  })
  const lotRows = useQuery<LotAgeingRow[]>({
    enabled: flavour === 'lot-wise',
    queryKey: ['ageing-lot', bucket, departmentId, machineId],
    queryFn: () => ageingApi.lotWise({
      bucket, department_id: departmentId, machine_id: machineId,
    }),
  })
  const slowRows = useQuery<ItemAgeingRow[]>({
    enabled: flavour === 'slow-moving',
    queryKey: ['ageing-slow'],
    queryFn: () => ageingApi.slowMoving(),
  })
  const deadRows = useQuery<ItemAgeingRow[]>({
    enabled: flavour === 'dead-stock',
    queryKey: ['ageing-dead'],
    queryFn: () => ageingApi.deadStock(),
  })
  const criticalRows = useQuery<ItemAgeingRow[]>({
    enabled: flavour === 'critical',
    queryKey: ['ageing-critical'],
    queryFn: () => ageingApi.criticalSpares(),
  })
  const highValRows = useQuery<ItemAgeingRow[]>({
    enabled: flavour === 'high-value',
    queryKey: ['ageing-high-value', minValue],
    queryFn: () => ageingApi.highValueOldStock({
      min_value: minValue, min_age_days: 180,
    }),
  })
  const machineRows = useQuery<MachineSpareAgeingRow[]>({
    enabled: flavour === 'machine-wise',
    queryKey: ['ageing-machine'],
    queryFn: () => ageingApi.machineWiseSpares(),
  })

  // Pick the right dataset
  let rows: Array<ItemAgeingRow | LotAgeingRow | MachineSpareAgeingRow> = []
  let isLoading = false
  switch (flavour) {
    case 'item-wise':    rows = itemRows.data ?? []; isLoading = itemRows.isLoading; break
    case 'lot-wise':     rows = lotRows.data ?? []; isLoading = lotRows.isLoading; break
    case 'slow-moving':  rows = slowRows.data ?? []; isLoading = slowRows.isLoading; break
    case 'dead-stock':   rows = deadRows.data ?? []; isLoading = deadRows.isLoading; break
    case 'critical':     rows = criticalRows.data ?? []; isLoading = criticalRows.isLoading; break
    case 'high-value':   rows = highValRows.data ?? []; isLoading = highValRows.isLoading; break
    case 'machine-wise': rows = machineRows.data ?? []; isLoading = machineRows.isLoading; break
  }

  const refetch = () => {
    switch (flavour) {
      case 'item-wise':    return itemRows.refetch()
      case 'lot-wise':     return lotRows.refetch()
      case 'slow-moving':  return slowRows.refetch()
      case 'dead-stock':   return deadRows.refetch()
      case 'critical':     return criticalRows.refetch()
      case 'high-value':   return highValRows.refetch()
      case 'machine-wise': return machineRows.refetch()
    }
  }

  const [exporting, setExporting] = useState(false)
  const exportCsv = async () => {
    setExporting(true)
    try {
      const pathMap: Record<typeof flavour, string> = {
        'item-wise':    '/item-wise',
        'lot-wise':     '/lot-wise',
        'slow-moving':  '/slow-moving',
        'dead-stock':   '/dead-stock',
        'critical':     '/critical-spares',
        'high-value':   '/high-value-old-stock',
        'machine-wise': '/machine-wise-spares',
      }
      const params: Record<string, unknown> = {}
      if (flavour === 'item-wise' || flavour === 'lot-wise') {
        if (bucket) params.bucket = bucket
        if (departmentId) params.department_id = departmentId
        if (machineId) params.machine_id = machineId
      }
      if (flavour === 'high-value') {
        params.min_value = minValue
      }
      await downloadAgeingCsv(
        pathMap[flavour], params,
        `${flavour}-ageing-${dayjs().format('YYYYMMDD-HHmm')}.csv`,
      )
    } catch (e) {
      message.error((e as Error).message ?? 'Export failed')
    } finally {
      setExporting(false)
    }
  }

  // ── Columns per flavour ────────────────────────────────────────────────
  const itemColumns = [
    { title: 'Item', dataIndex: 'item_code', key: 'ic', width: 130 },
    { title: 'Name', dataIndex: 'item_name', key: 'in', ellipsis: true },
    { title: 'Category', dataIndex: 'category', key: 'cat', width: 120 },
    {
      title: 'Stock', dataIndex: 'current_stock', key: 'cs', width: 100,
      render: (v: number, r: ItemAgeingRow) => `${v.toFixed(2)} ${r.uom}`,
    },
    {
      title: 'Rate', dataIndex: 'average_rate', key: 'r', width: 120,
      render: (v: number) => `₹ ${v.toFixed(2)}`,
    },
    {
      title: 'Value', dataIndex: 'stock_value', key: 'v', width: 130,
      render: (v: number) => <Text strong>₹ {v.toFixed(2)}</Text>,
    },
    {
      title: 'Oldest age', dataIndex: 'oldest_age_days', key: 'oa', width: 110,
      render: (v: number) => `${v}d`,
    },
    {
      title: 'Bucket / Status', key: 'st', width: 220,
      render: (_: unknown, r: ItemAgeingRow) => (
        <AgeingBucketBadge bucket={r.ageing_bucket} status={r.stock_status} />
      ),
    },
    {
      title: 'Last issue', dataIndex: 'last_issue_date', key: 'li', width: 130,
      render: (v: string | null) => v ? dayjs(v).format('DD MMM YYYY') : '—',
    },
    {
      title: 'Critical?', dataIndex: 'critical_spare_flag', key: 'cr', width: 90,
      render: (v: boolean) => v ? <Tag color="purple">YES</Tag> : null,
    },
  ]

  const lotColumns = [
    { title: 'Lot ID', dataIndex: 'stock_lot_id', key: 'l', width: 130,
      render: (v: string) => <Text code>{v.slice(0, 8)}</Text>,
    },
    { title: 'Item', dataIndex: 'item_code', key: 'ic', width: 130 },
    { title: 'Name', dataIndex: 'item_name', key: 'in', ellipsis: true },
    {
      title: 'Received', dataIndex: 'received_qty', key: 'rq', width: 100,
      render: (v: number) => v.toFixed(2),
    },
    {
      title: 'Issued', dataIndex: 'issued_qty', key: 'iq', width: 100,
      render: (v: number) => v.toFixed(2),
    },
    {
      title: 'Balance', dataIndex: 'balance_qty', key: 'bq', width: 100,
      render: (v: number) => v.toFixed(2),
    },
    {
      title: 'Landed Rate', dataIndex: 'landed_rate', key: 'lr', width: 130,
      render: (v: number) => `₹ ${v.toFixed(4)}`,
    },
    {
      title: 'Balance Value', dataIndex: 'balance_value', key: 'bv', width: 130,
      render: (v: number) => <Text strong>₹ {v.toFixed(2)}</Text>,
    },
    {
      title: 'Receipt date', dataIndex: 'receipt_date', key: 'rd', width: 130,
      render: (v: string | null) => v ? dayjs(v).format('DD MMM YYYY') : '—',
    },
    {
      title: 'Age', dataIndex: 'age_days', key: 'a', width: 80,
      render: (v: number) => `${v}d`,
    },
    {
      title: 'Bucket', key: 'b', width: 130,
      render: (_: unknown, r: LotAgeingRow) =>
        <AgeingBucketBadge bucket={r.ageing_bucket} />,
    },
    {
      title: 'Bin', dataIndex: 'bin_location', key: 'bin', width: 120,
    },
  ]

  const machineColumns = [
    { title: 'Machine', dataIndex: 'machine_code', key: 'mc', width: 130 },
    { title: 'Spare', dataIndex: 'spare_code', key: 's', width: 130 },
    { title: 'Name', dataIndex: 'spare_name', key: 'sn', ellipsis: true },
    {
      title: 'Stock', dataIndex: 'current_stock', key: 'cs', width: 100,
      render: (v: number) => v.toFixed(2),
    },
    {
      title: 'Value', dataIndex: 'balance_value', key: 'bv', width: 130,
      render: (v: number) => `₹ ${v.toFixed(2)}`,
    },
    {
      title: 'Age', dataIndex: 'oldest_age_days', key: 'a', width: 80,
      render: (v: number) => `${v}d`,
    },
    {
      title: 'Last used', dataIndex: 'last_used_date', key: 'lu', width: 130,
      render: (v: string | null) => v ? dayjs(v).format('DD MMM YYYY') : '—',
    },
    {
      title: 'Used 90 / 180 days', key: 'u', width: 150,
      render: (_: unknown, r: MachineSpareAgeingRow) =>
        `${r.usage_count_90_days} / ${r.usage_count_180_days}`,
    },
    {
      title: 'Status', dataIndex: 'stock_status', key: 'st', width: 200,
      render: (v: MachineSpareAgeingRow['stock_status']) =>
        <AgeingBucketBadge status={v} />,
    },
  ]

  const columns =
    flavour === 'lot-wise' ? lotColumns
      : flavour === 'machine-wise' ? machineColumns
        : itemColumns
  const rowKey =
    flavour === 'lot-wise' ? 'stock_lot_id'
      : flavour === 'machine-wise' ? (r: MachineSpareAgeingRow) =>
          `${r.machine_id}-${r.spare_id}`
        : 'spare_id'

  // KPI strip
  const totalValue = rows.reduce((a, r) =>
    a + ((r as ItemAgeingRow).stock_value
        ?? (r as LotAgeingRow).balance_value
        ?? (r as MachineSpareAgeingRow).balance_value
        ?? 0), 0)

  // Show filters depending on flavour
  const showBucket = flavour === 'item-wise' || flavour === 'lot-wise'
  const showDeptMachine = flavour === 'item-wise' || flavour === 'lot-wise'
  const showMinValue = flavour === 'high-value'

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle} />

      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          {showBucket && (
            <Select
              allowClear
              placeholder="Bucket"
              style={{ width: 150 }}
              options={BUCKETS.map((b) => ({ label: b, value: b }))}
              value={bucket}
              onChange={setBucket}
            />
          )}
          {showDeptMachine && (
            <>
              <Select
                allowClear placeholder="Department" style={{ width: 200 }}
                options={(depts?.items ?? []).map((d) => ({
                  label: d.name, value: d.id,
                }))}
                value={departmentId}
                onChange={setDepartmentId}
              />
              <Select
                allowClear showSearch placeholder="Machine"
                style={{ width: 220 }}
                options={(machines?.items ?? []).map((m) => ({
                  label: `${m.machine_id_str} — ${m.name}`, value: m.id,
                }))}
                filterOption={(i, o) =>
                  (o?.label as string).toLowerCase().includes(i.toLowerCase())
                }
                value={machineId}
                onChange={setMachineId}
              />
            </>
          )}
          {showMinValue && (
            <Space>
              <Text>Min ₹</Text>
              <InputNumber
                min={0} step={1000}
                value={minValue}
                onChange={(v) => setMinValue(Number(v) || 0)}
              />
            </Space>
          )}
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>Refresh</Button>
          <Button icon={<DownloadOutlined />} onClick={exportCsv} loading={exporting}>
            Export CSV
          </Button>
        </Space>
      </Card>

      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}>
          <Card><Statistic title="Rows" value={rows.length} /></Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Total value"
              value={totalValue}
              prefix="₹"
              precision={2}
            />
          </Card>
        </Col>
      </Row>

      {/* DataTable is generic per-flavour; cast to satisfy the type system
          since this single component handles three row shapes. */}
      <DataTable<AnyRow>
        rowKey={rowKey as string}
        columns={columns as ColumnsType<AnyRow>}
        dataSource={rows as AnyRow[]}
        loading={isLoading}
      />
    </div>
  )
}
